import { ChangeEvent } from 'react';
import { Autocomplete, CircularProgress, MenuItem } from '@mui/material';
import { Currency } from '../../../../../../pages/dashboard/payments/interfaces/Currency';
import { formatDecimalNumber } from '../../../../../../utils/helper';
import Customdatepicker from '../../../../../customdatepicker/customdatepicker';
import CustomTextField from '../../../../../CustomTextInput';
import Signature from '../../../../../signature/signature';
import {
    Quotation,
    QuotationFormErrors
} from '../../../../../../interfaces/Quotation';

interface QuotationStepTwoFormProps {
    updateField: (name: string, value: string | number) => void;
    payload: Quotation;
    errors: QuotationFormErrors;
    currencies: Currency[];
    loadingCurrencies: boolean;
    setSignatureUrlBase64: (value: string) => void;
    signatureUrlBase64: string;
    totalAmount: number;
    onSaveOnlyAction?: () => void;
    showPreview: boolean;
    setShowPreview: (val: boolean) => void;
}

const StepTwo = ({
    updateField,
    payload,
    errors,
    currencies,
    loadingCurrencies,
    setSignatureUrlBase64,
    signatureUrlBase64,
    totalAmount
}: QuotationStepTwoFormProps) => {
    return (
        <div className='w-full flex flex-col gap-4'>
            <Autocomplete
                value={payload.currencyCodeValue as unknown as Currency}
                placeholder='Currency'
                disableClearable
                id='send-currency'
                renderInput={(params) => (
                    <CustomTextField
                        params={params}
                        value={payload.currencyCode}
                        label='Select Currency'
                        placeholder='Currency'
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: loadingCurrencies && (
                                <>
                                    <CircularProgress size={20} />
                                    {params.InputProps.endAdornment}
                                </>
                            )
                        }}
                    />
                )}
                noOptionsText={'No data available'}
                renderOption={(props, option: Currency) => {
                    return (
                        <MenuItem
                            {...props}
                            value={option.name}
                            key={`countries-${option.id}`}
                        >
                            {option.name}
                        </MenuItem>
                    );
                }}
                getOptionLabel={(option: Currency) => option.name}
                onChange={(_, value: Currency) => {
                    if (value !== null) {
                        updateField('currencyCode', value.currencyCode);
                        updateField(
                            'currencyCodeValue',
                            value as unknown as string
                        );
                    }
                }}
                options={currencies}
            />

            <CustomTextField
                disabled
                id='quotation-totalAmount'
                label='Total Amount'
                name='totalAmount'
                value={formatDecimalNumber(totalAmount)}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('totalAmount', e.target.value);
                }}
            />

            <CustomTextField
                value={payload?.additionalInformation}
                multiline
                rows={7.4}
                height={156}
                id='quotation-additionalInformation'
                label='Additional Information'
                name='additionalInformation'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('additionalInformation', e.target.value);
                }}
            />

            <CustomTextField
                value={payload?.placeOfIssue}
                error={errors?.placeOfIssue.length > 0}
                helperText={
                    errors?.placeOfIssue.length > 0
                        ? errors?.placeOfIssue
                        : undefined
                }
                required
                id='quotation-placeOfIssue'
                label='Place of Issue'
                name='placeOfIssue'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('placeOfIssue', e.target.value);
                }}
            />

            <Customdatepicker
                id='quotation-dateOfIssue'
                label='Date of Issue'
                name='dateOfIssue'
                updateField={updateField}
                error={errors?.dateOfIssue.length > 0}
                helperText={
                    errors?.dateOfIssue.length > 0
                        ? errors?.dateOfIssue
                        : undefined
                }
                value={
                    payload?.dateOfIssue !== undefined
                        ? payload?.dateOfIssue
                        : null
                }
            />

            <CustomTextField
                value={payload?.signatoryCompany}
                error={errors?.signatoryCompany.length > 0}
                helperText={
                    errors?.signatoryCompany.length > 0
                        ? errors?.signatoryCompany
                        : undefined
                }
                required
                id='quotation-signatoryCompany'
                label='Signatory Company'
                name='signatoryCompany'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('signatoryCompany', e.target.value);
                }}
            />

            <CustomTextField
                value={payload?.nameOfAuthorisedSignatory}
                error={errors?.nameOfAuthorisedSignatory.length > 0}
                helperText={
                    errors?.nameOfAuthorisedSignatory.length > 0
                        ? errors?.nameOfAuthorisedSignatory
                        : undefined
                }
                required
                id='quotation-nameOfAuthorisedSignatory'
                label='Name of Authorized Signature'
                name='nameOfAuthorisedSignatory'
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    updateField('nameOfAuthorisedSignatory', e.target.value);
                }}
            />

            <Signature
                name='signatureUrl'
                updateField={updateField}
                setSignatureUrlBase64={setSignatureUrlBase64}
                signatureUrlBase64={signatureUrlBase64}
            />
        </div>
    );
};

export default StepTwo;
